import { type Graph } from '../models/graph';

export interface TspResult {
  readonly tourEdgeIds: readonly string[];
  readonly length: number;
}

const MAX_SUPPORTED_NODES = 12;

function edgeKey(a: string, b: string): string {
  return a <= b ? `${a}_${b}` : `${b}_${a}`;
}

export function optimalTour(graph: Graph): TspResult | null {
  const n = graph.nodes.length;
  if (n > MAX_SUPPORTED_NODES) {
    throw new Error(
      `optimalTour supports at most ${MAX_SUPPORTED_NODES} nodes; received ${n}. ` +
        'Use a heuristic for larger instances.',
    );
  }
  if (n < 3) return null;

  const weightMap = new Map<string, number>();
  const edgeMap = new Map<string, string>();
  for (const edge of graph.edges) {
    const key = edgeKey(edge.sourceId, edge.targetId);
    weightMap.set(key, edge.weight);
    edgeMap.set(key, edge.id);
  }

  const nodeIds = graph.nodes.map((node) => node.id);
  const startId = nodeIds[0]!;
  const remaining = nodeIds.slice(1);

  let bestLength = Infinity;
  let bestPath: string[] | null = null;

  function dfs(current: string, visited: string[], currentLength: number): void {
    if (currentLength >= bestLength) return; // prune

    if (visited.length === n - 1) {
      const closeKey = edgeKey(current, startId);
      const closeWeight = weightMap.get(closeKey);
      if (closeWeight === undefined) return;
      const totalLength = currentLength + closeWeight;
      if (totalLength < bestLength) {
        bestLength = totalLength;
        bestPath = [...visited];
      }
      return;
    }

    for (const next of remaining) {
      if (visited.includes(next)) continue;
      const weight = weightMap.get(edgeKey(current, next));
      if (weight === undefined) continue;

      visited.push(next);
      dfs(next, visited, currentLength + weight);
      visited.pop();
    }
  }

  dfs(startId, [], 0);
  if (!bestPath) return null;

  const tourNodes = [startId, ...(bestPath as string[]), startId];
  const tourEdgeIds: string[] = [];
  for (let i = 0; i < tourNodes.length - 1; i++) {
    tourEdgeIds.push(edgeMap.get(edgeKey(tourNodes[i]!, tourNodes[i + 1]!))!);
  }

  return { tourEdgeIds, length: bestLength };
}

export function countHamiltonianCycles(graph: Graph, limit = 8): number {
  const n = graph.nodes.length;
  if (n > MAX_SUPPORTED_NODES) {
    throw new Error(
      `countHamiltonianCycles supports at most ${MAX_SUPPORTED_NODES} nodes; received ${n}.`,
    );
  }
  if (n < 3) return 0;

  const adjacency = new Map<string, Set<string>>();
  for (const edge of graph.edges) {
    if (!adjacency.has(edge.sourceId)) adjacency.set(edge.sourceId, new Set());
    if (!adjacency.has(edge.targetId)) adjacency.set(edge.targetId, new Set());
    adjacency.get(edge.sourceId)!.add(edge.targetId);
    adjacency.get(edge.targetId)!.add(edge.sourceId);
  }

  const nodeIds = graph.nodes.map((node) => node.id);
  const startId = nodeIds[0]!;
  const remaining = nodeIds.slice(1);

  const seen = new Set<string>();
  let count = 0;

  function dfs(current: string, visited: string[]): void {
    if (count >= limit) return;

    if (visited.length === n - 1) {
      if (!adjacency.get(current)?.has(startId)) return;
      const sig = canonicalCycle([startId, ...visited]);
      if (!seen.has(sig)) {
        seen.add(sig);
        count++;
      }
      return;
    }

    for (const next of remaining) {
      if (visited.includes(next)) continue;
      if (!adjacency.get(current)?.has(next)) continue;
      visited.push(next);
      dfs(next, visited);
      visited.pop();
      if (count >= limit) return;
    }
  }

  dfs(startId, []);
  return count;
}

function canonicalCycle(cycle: readonly string[]): string {
  const n = cycle.length;

  let minIdx = 0;
  for (let i = 1; i < n; i++) {
    if (cycle[i]! < cycle[minIdx]!) minIdx = i;
  }

  const forward: string[] = [];
  for (let i = 0; i < n; i++) forward.push(cycle[(minIdx + i) % n]!);

  const reverse: string[] = [forward[0]!];
  for (let i = n - 1; i >= 1; i--) reverse.push(forward[i]!);

  const fwdStr = forward.join(',');
  const revStr = reverse.join(',');
  return fwdStr <= revStr ? fwdStr : revStr;
}
